import React from 'react';
import { motion } from 'motion/react';
import {
  Bot,
  Calendar,
  ShoppingCart,
  BarChart3,
  Zap,
  Users,
  CheckCircle,
  Sparkles
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { useWhatsAppDemo } from '../../hooks/useWhatsAppDemo';
import { fadeInUp, staggerContainer, staggerItem, hoverLift } from '../../lib/motion';

interface Feature {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  benefits: string[];
}

const Features: React.FC = () => {
  const requestDemo = useWhatsAppDemo();

  const features: Feature[] = [
    {
      icon: Bot,
      title: 'AI Conversazionale Avanzata',
      description: 'Risposte intelligenti che capiscono il contesto e la personalità del tuo brand',
      benefits: ['NLP avanzato', 'Apprendimento continuo', 'Risposte personalizzate']
    },
    {
      icon: Calendar,
      title: 'Prenotazioni Automatiche',
      description: 'Gestione appuntamenti integrata con Google Calendar e sistemi di booking',
      benefits: ['Sincronizzazione Calendar', 'Promemoria automatici', 'Gestione disponibilità']
    },
    {
      icon: ShoppingCart,
      title: 'E-commerce Integration',
      description: 'Ordini e pagamenti direttamente su WhatsApp con carrello intelligente',
      benefits: ['Catalogo prodotti', 'Pagamenti sicuri', 'Tracking ordini']
    },
    {
      icon: BarChart3,
      title: 'Analytics Dettagliati',
      description: 'Dashboard con KPI, performance insights e reportistica avanzata',
      benefits: ['Metriche in tempo reale', 'Report personalizzati', 'Export dati']
    },
    {
      icon: Zap,
      title: 'Integrazioni CRM',
      description: 'Connetti seamless con i tuoi sistemi esistenti e database clienti',
      benefits: ['API potenti', 'Sync bidirezionale', 'Workflow automatici']
    },
    {
      icon: Users,
      title: 'Multi-account Management',
      description: 'Gestisci più numeri WhatsApp da un\'unica dashboard centralizzata',
      benefits: ['Dashboard unificata', 'Permessi team', 'Gestione multi-brand']
    }
  ];

  return (
    <section id="features" className="py-16 lg:py-24 bg-background">
      <div className="container mx-auto px-4">
        <motion.div
          {...fadeInUp}
          className="text-center mb-16"
        >
          <h2 className="text-3xl lg:text-5xl font-heading font-bold text-text-primary mb-6">
            Funzionalità che{' '}
            <span className="text-primary">
              Rivoluzionano
            </span>{' '}
            il tuo Business
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed">
            Tutto quello che ti serve per automatizzare le conversazioni WhatsApp e aumentare le vendite
          </p>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {features.map((feature) => (
            <motion.div
              key={feature.title}
              variants={staggerItem}
              whileHover={{ y: -6 }}
              transition={hoverLift}
            >
              <Card className="h-full bg-card border border-border rounded-2xl shadow-sm hover:shadow-md hover:border-primary/30 transition-all duration-300 group">
                <CardContent className="p-8">
                  <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
                    <feature.icon className="h-7 w-7 text-primary" aria-hidden="true" />
                  </div>

                  <h3 className="text-xl font-heading font-bold text-text-primary mb-3 group-hover:text-primary transition-colors duration-300">
                    {feature.title}
                  </h3>

                  <p className="text-text-secondary mb-6 leading-relaxed">
                    {feature.description}
                  </p>

                  <ul className="space-y-2">
                    {feature.benefits.map((benefit, benefitIndex) => (
                      <li key={benefitIndex} className="flex items-center text-sm text-text-secondary">
                        <CheckCircle className="h-4 w-4 text-success mr-2 flex-shrink-0" aria-hidden="true" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          {...fadeInUp}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="text-center mt-16"
        >
          <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-3xl p-8 lg:p-12">
            <h3 className="text-2xl lg:text-3xl font-heading font-bold text-text-primary mb-4">
              Pronto a vedere ChatLy in azione?
            </h3>
            <p className="text-lg text-text-secondary mb-8 max-w-2xl mx-auto">
              Richiedi una demo personalizzata e scopri come queste funzionalità possono trasformare il tuo business
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={requestDemo}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-primary to-primary-dark text-white font-bold py-4 px-8 rounded-full text-lg shadow-xl hover:shadow-2xl transition-all duration-300"
            >
              <Sparkles className="h-5 w-5" aria-hidden="true" />
              Richiedi Demo Gratuita
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Features;
